import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, Leaf, Sprout, Crown, Check, Zap, Infinity, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import { LegalFooter } from '@/components/LegalFooter';

const FREE_FEATURES = [
  'Up to 5 plants',
  'Personalised schedule',
  'Calendar & list views',
  'Task tracking',
];

const PRO_FEATURES = [
  'Unlimited plants',
  'All plant types',
  'Full schedule generation',
  'Priority support',
];

export default function Pricing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/40 bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-xl">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl garden-gradient shadow-soft">
              <Leaf className="h-4 w-4 text-primary-foreground" />
            </div>
            <h1 className="text-lg font-bold text-foreground tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Pricing
            </h1>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 pb-24 space-y-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center space-y-3"
        >
          <h2 className="text-3xl font-bold text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            Pick your plot 🌱
          </h2>
          <p className="text-muted-foreground text-lg max-w-md mx-auto">
            Start free, upgrade when you're ready to grow more.
          </p>
        </motion.div>

        {/* Plans */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="grid gap-5 sm:grid-cols-2"
        >
          {/* Free Plan */}
          <Card className="border-2 border-border">
            <CardContent className="pt-6 pb-5 space-y-5">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Sprout className="h-5 w-5 text-muted-foreground" />
                  <span className="font-bold text-foreground text-lg">Free</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-foreground">£0</span>
                  <span className="text-sm text-muted-foreground">/forever</span>
                </div>
                <p className="text-xs text-muted-foreground">Perfect for getting started</p>
              </div>

              <ul className="space-y-2.5">
                {FREE_FEATURES.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 text-muted-foreground shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <Button onClick={() => navigate('/auth')} variant="outline" className="w-full rounded-xl">
                Get Started
              </Button>
            </CardContent>
          </Card>

          {/* Pro Plan */}
          <Card className="border-2 border-primary/30 relative overflow-hidden">
            <div className="absolute top-3 right-3">
              <Badge className="bg-primary text-primary-foreground text-[10px]">Popular</Badge>
            </div>
            <CardContent className="pt-6 pb-5 space-y-5">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-primary" />
                  <span className="font-bold text-foreground text-lg">Pro</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-foreground">£3.99</span>
                  <span className="text-sm text-muted-foreground">/month</span>
                </div>
                <p className="text-xs text-primary font-medium">or £33/year (save 31%)</p>
              </div>

              <ul className="space-y-2.5">
                {PRO_FEATURES.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-foreground">
                    <Check className="h-4 w-4 text-primary shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <Button onClick={() => navigate('/auth')} className="w-full garden-gradient rounded-xl">
                Start Free, Upgrade Later
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* FAQ-like note */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center space-y-2"
        >
          <p className="text-sm text-muted-foreground">
            No commitment. Cancel anytime. Your garden data stays yours.
          </p>
        </motion.div>
      </main>

      <LegalFooter />
    </div>
  );
}
